// src/components/resources/tools/generators/FakeChat/useResolvedMessages.ts

'use client'

import { useMemo } from 'react'

import type { ChatMessage, ChatParticipant, MessageStatus } from './FakeChat.types'
import { useFakeChatSelectors } from './FakeChatContext'

export type ResolvedMessage = {
  message: ChatMessage
  participant?: ChatParticipant
  isContinuation: boolean
  deliveryStatus: MessageStatus
  /**
   * Whether this message belongs to the viewer ("outgoing"). Derived once
   * here from the participant's `isSelf` flag, falling back to right
   * alignment, so every platform preview shares one definition instead of
   * re-deriving the same `participant?.isSelf ?? alignment === 'right'`
   * expression in each bubble component.
   */
  isSelf: boolean
}

/**
 * Shared helper for joining the current participant roster with the message list.
 * Each preview previously memoised the same lookup logic which made fine tuned
 * layout updates harder to audit. Centralising the hook keeps the existing
 * behaviour identical – we still derive continuation groupings per render – but
 * gives every platform view a single source of truth.
 */
export function useResolvedMessages(messages: ChatMessage[]): ResolvedMessage[] {
  const selectors = useFakeChatSelectors()

  return useMemo(() => {
    return messages.map((message, index) => {
      const previous = messages[index - 1]
      const isContinuation = previous?.participantId === message.participantId
      const participant = selectors.getParticipantById(message.participantId)

      return {
        message,
        participant,
        isContinuation,
        deliveryStatus: message.status ?? 'delivered',
        isSelf: participant?.isSelf ?? message.alignment === 'right'
      }
    })
  }, [messages, selectors])
}

